import { BookingParty } from 'features/BookingFlow'
import { useState } from 'react'
import { isEuropeCountryCode, ISO } from 'service/ISO'

import {
  Container,
  createPackingItem,
  IShipperContact,
  PackingListItem,
  ShipmentInterface,
  ShipmentRequestBody,
} from '../domain/ShipmentInterface'
import { pickup } from './ShipmentUtils'

const fieldsToJS = [
  'additionalInformation',
  'containers',
  'customerReference',
  'deliveryDate',
  'deliveryDetails',
  'deliveryDetailsChanged',
  'destination',
  'eoriNumber',
  'haulageInstructions',
  'hblType',
  'incoTerms',
  'numberOfPackages',
  'origin',
  'packingLists',
  'packingListsCopy',
  'pickupDetails',
  'volume',
  'weight',
]

export type UseShipmentStore = {
  shipment: ShipmentInterface
  processResponse: (shipmentResponse: ShipmentInterface) => ShipmentInterface
  isOriginEUCountry: boolean
  isCanceled: boolean
  resetContainers: () => void
  removeRequiredAction: (actionType: string) => void
  updateIncoterm: (incoTerm: string) => void
  updateBookingParty: (bookingParty: string) => void
  getRequestBody: () => ShipmentRequestBody
  updateShipmentDetails: (value: Partial<ShipmentInterface>) => void
}

export const useShipmentStore = (): UseShipmentStore => {
  const [shipment, setShipment] = useState<ShipmentInterface | null>(null)
  const [containers, setContainers] = useState<Container[]>([])

  // @ts-expect-error TS(2345) FIXME: Argument of type 'ShipmentInterface | null' is not... Remove this comment to see the full error message
  const isOriginEUCountry = isEuropeCountryCode(pickup(shipment)?.countryCode)
  const isCanceled: boolean = shipment?.state === 'canceled'

  const containersFromResponse = (containersResponse: Container[]) => {
    const defaultContainerProps = {
      type: '',
      containerNumber: '',
      sealNumber: '',
      grossWeight: null,
      customerReference: '',
    }

    if (containersResponse && containersResponse.length > 0) {
      return containersResponse.map((containerResponse, index) => {
        const oldContainer = containers.length ? containers[index] : { ...defaultContainerProps }

        // Reassigning the existing object with new object in the array
        // This is done to ensure that reference is not lost
        return Object.assign(oldContainer, containerResponse)
      })
    }
    return []
  }

  const contactFromResponse = (shipperContact: IShipperContact) => {
    return {
      companyName: '',
      contactPerson: '',
      address: '',
      phone: '',
      email: '',
      ...shipperContact,
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
  const packingListsFromResponse = (packingLists: PackingListItem[][], containers: any[]): PackingListItem[][] => {
    if (containers && containers.length > 0) {
      return containers.map((c, i) => {
        if (packingLists[i] && packingLists[i].length) {
          return packingLists[i].map((item) => createPackingItem(item))
        }
        return [] as PackingListItem[]
      })
    }
    if (packingLists && packingLists.length > 0) {
      return packingLists
    }
    return [[]] as PackingListItem[][]
  }

  const packingListsCopyFromResponse = (
    packingListsCopy: Array<number | boolean>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
    containers: any[]
  ): Array<number | boolean> => {
    if (containers && containers.length > 0) {
      return containers.map((pl, i) => {
        if (packingListsCopy[i] == null) {
          return false
        }
        return packingListsCopy[i]
      })
    }
    return []
  }

  const removeRequiredAction = (actionType: string) => {
    // @ts-expect-error TS(2345) FIXME: Argument of type '{ requiredActions: RequiredActio... Remove this comment to see the full error message
    setShipment({
      ...shipment,
      // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
      requiredActions: shipment.requiredActions.filter((action) => action.type !== actionType),
    })
  }

  const updateIncoterm = (incoTerm: string) => {
    // @ts-expect-error TS(2345) FIXME: Argument of type '{ quoteOption: { incoTerm: strin... Remove this comment to see the full error message
    setShipment({
      ...shipment,
      quoteOption: {
        // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
        ...shipment.quoteOption,
        incoTerm: incoTerm as ISO.IncoTermsCode,
      },
    })
  }

  const updateBookingParty = (bookingParty: string) => {
    // @ts-expect-error TS(2345) FIXME: Argument of type '{ quoteOption: { bookingParty: B... Remove this comment to see the full error message
    setShipment({
      ...shipment,
      quoteOption: {
        // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
        ...shipment.quoteOption,
        bookingParty: bookingParty as BookingParty,
      },
    })
  }

  const resetContainers = () => {
    setContainers([])
  }

  const updateShipmentDetails = (value: Partial<ShipmentInterface>) => {
    // @ts-expect-error TS(2345) FIXME: Argument of type '{ _id?: string | undefined; book... Remove this comment to see the full error message
    setShipment({ ...shipment, ...value })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
  const processResponse = (shipmentResponse: any): ShipmentInterface => {
    return {
      ...shipmentResponse,
      customerReference: shipmentResponse.customerReference || '',
      containers: containersFromResponse(shipmentResponse.containers),
      incoTerms: shipmentResponse.incoTerms || '',
      additionalInformation: shipmentResponse.additionalInformation || '',
      hblType: shipmentResponse.hblType || '',
      documents: shipmentResponse.documents,
      documents_id: shipmentResponse.documents_id,
      shipperContact: contactFromResponse(shipmentResponse.shipperContact),
      consigneeContact: contactFromResponse(shipmentResponse.consigneeContact),
      numberOfPackages: shipmentResponse.numberOfPackages,
      volume: shipmentResponse.volume,
      weight: shipmentResponse.weight,
      deliveryDate: shipmentResponse.deliveryDate ? new Date(shipmentResponse.deliveryDate) : null,
      packingLists: packingListsFromResponse(shipmentResponse.packingLists, shipmentResponse.containers),
      packingListsCopy: packingListsCopyFromResponse(shipmentResponse.packingListsCopy, shipmentResponse.containers),
      pickupDetails: shipmentResponse.pickupDetails || {
        companyName: '',
        street: '',
        streetNumber: '',
        city: '',
        postalCode: '',
        country: '',
        instructions: '',
      },
      deliveryDetails: shipmentResponse.deliveryDetails || {
        companyName: '',
        street: '',
        streetNumber: '',
        city: '',
        postalCode: '',
        country: '',
        instructions: '',
      },
      requiredActions: shipmentResponse.requiredActions,
      opsAdditionalInformation: shipmentResponse.opsAdditionalInformation || '',
      opsData: shipmentResponse.opsData || undefined,
      createdAt: shipmentResponse.createdAt || '',
      buyersConsolidation: shipmentResponse.buyersConsolidation,
      quoteSheetId: shipmentResponse.quoteSheet_id,
    }
  }

  const getRequestBody = () => {
    return fieldsToJS.reduce(
      (accum, field) => {
        // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
        accum[field] = shipment[field]
        return accum
      },
      {
        // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
        transportScheme: shipment.tp,
      }
    )
  }

  return {
    // @ts-expect-error TS(2322) FIXME: Type 'ShipmentInterface | null' is not assignable ... Remove this comment to see the full error message
    shipment,
    processResponse,
    isOriginEUCountry,
    isCanceled,
    resetContainers,
    removeRequiredAction,
    updateIncoterm,
    updateShipmentDetails,
    updateBookingParty,
    getRequestBody,
  }
}
